/**
 * Editable catalog for the account request form (`/form`).
 *
 * Structure: **Concept** (dropdown 1) → **Location** (dropdown 2, filtered by concept).
 * Each row is one selectable site; `name` is what staff see. `storeId` must match
 * `stores.store_id` in your DB (JIT lookup). Codes below are stable placeholders —
 * change them to your real provisioning IDs if they differ.
 *
 * For Margaritaville alignment, DB seeding, and release order, see repository
 * `PHASE3_TODO_AND_TEST_PLAN.md` and related phase runbooks in the project root.
 */
import type { UserType } from "../types";

export interface JobTitleDefinition {
  id: string;
  label: string;
  /** Shown read-only after the user picks a title; sent to Workvivo as enterprise department */
  department: string;
  userType: UserType;
}

export interface CatalogLocation {
  id: string;
  name: string;
  /** Maps to `stores.store_id` for JIT validation and team assignment */
  storeId: string;
}

export interface CatalogBrand {
  id: string;
  name: string;
  locations: CatalogLocation[];
}

/** Brand groups and locations (from master property list). Order is UI order. */
export const MARGARITAVILLE_BRANDS: CatalogBrand[] = [
  {
    id: "camp-margaritaville",
    name: "Camp Margaritaville",
    locations: [
      { id: "cm-auburndale", name: "Resort & Cabana Cabins — Auburndale", storeId: "CM-AUBURNDALE" },
      { id: "cm-breaux-bridge", name: "RV Resort — Breaux Bridge", storeId: "CM-BREAUX-BRIDGE" },
      { id: "cm-crystal-beach", name: "Resort — Crystal Beach", storeId: "CM-CRYSTAL-BEACH" },
      { id: "cm-orange-lake", name: "Resort — Orange Lake", storeId: "CM-ORANGE-LAKE" },
      { id: "cm-lanier", name: "Resort — Lanier Islands", storeId: "CM-LANIER-ISLANDS" },
      { id: "cm-pigeon-forge", name: "Resort — Pigeon Forge", storeId: "CM-PIGEON-FORGE" }
    ]
  },
  {
    id: "compass-hotel",
    name: "Compass Hotel",
    locations: [
      { id: "cp-anna-maria", name: "Anna Maria Sound", storeId: "CP-ANNA-MARIA-SOUND" },
      { id: "cp-beaufort", name: "Beaufort", storeId: "CP-BEAUFORT" },
      { id: "cp-cape-canaveral", name: "Cape Canaveral", storeId: "CP-CAPE-CANAVERAL" },
      { id: "cp-flagler-beach", name: "Flagler Beach", storeId: "CP-FLAGLER-BEACH" },
      { id: "cp-medford", name: "Medford", storeId: "CP-MEDFORD" },
      { id: "cp-naples", name: "Naples", storeId: "CP-NAPLES" },
      { id: "cp-nassau-bay", name: "Nassau Bay", storeId: "CP-NASSAU-BAY" },
      { id: "cp-north-myrtle", name: "North Myrtle Beach", storeId: "CP-N-MYRTLE-BEACH" },
      { id: "cp-pigeon-forge", name: "Pigeon Forge", storeId: "CP-PIGEON-FORGE" }
    ]
  },
  {
    id: "margaritaville-hotels-resorts",
    name: "Margaritaville Hotels & Resorts",
    locations: [
      { id: "mhr-lake-tahoe", name: "Lake Tahoe", storeId: "MHR-LAKE-TAHOE" },
      { id: "mhr-san-diego-gaslamp", name: "San Diego — Gaslamp Quarter", storeId: "MHR-SD-GASLAMP" },
      { id: "mhr-kansas-city", name: "Kansas City", storeId: "MHR-KANSAS-CITY" },
      { id: "mhr-ozarks", name: "Lake Resort — Lake of the Ozarks", storeId: "MHR-LAKE-OZARKS" },
      { id: "mhr-cape-cod", name: "Cape Cod", storeId: "MHR-CAPE-COD" },
      { id: "mhr-times-square", name: "Times Square", storeId: "MHR-TIMES-SQUARE" },
      {
        id: "mhr-pcb-cottage",
        name: "Beach Cottage Resort — Panama City Beach",
        storeId: "MHR-PCB-COTTAGE"
      },
      { id: "mhr-nashville", name: "Nashville", storeId: "MHR-NASHVILLE" },
      { id: "mhr-ft-myers", name: "Beach Resort — Fort Myers Beach", storeId: "MHR-FT-MYERS-BCH" },
      { id: "mhr-jax-beach", name: "Beach Hotel — Jacksonville Beach", storeId: "MHR-JAX-BEACH" },
      { id: "mhr-key-west", name: "Beach House — Key West", storeId: "MHR-KEY-WEST-HOUSE" },
      { id: "mhr-gatlinburg", name: "Resort — Gatlinburg", storeId: "MHR-GATLINBURG" },
      { id: "mhr-conroe", name: "Lake Resort — Lake Conroe | Houston", storeId: "MHR-LAKE-CONROE" },
      { id: "mhr-orlando", name: "Resort Orlando & Cottages", storeId: "MHR-ORLANDO" },
      { id: "mhr-pf-island-hotel", name: "Island Hotel — Pigeon Forge", storeId: "MHR-PF-ISLAND-HOTEL" },
      { id: "mhr-pf-island-inn", name: "Island Inn — Pigeon Forge", storeId: "MHR-PF-ISLAND-INN" },
      { id: "mhr-south-padre", name: "Beach Resort — South Padre Island", storeId: "MHR-SOUTH-PADRE" },
      { id: "mhr-hollywood-beach", name: "Beach Resort — Hollywood Beach", storeId: "MHR-HOLLYWOOD-BCH" },
      { id: "mhr-bossier", name: "Bossier City Resort Casino", storeId: "MHR-BOSSIER-CASINO" },
      { id: "mhr-biloxi", name: "Resort — Biloxi", storeId: "MHR-BILOXI" },
      { id: "mhr-orange-beach", name: "Resort — Orange Beach The Wharf", storeId: "MHR-ORANGE-BEACH" },
      {
        id: "mhr-galveston-cottage",
        name: "Beach Cottage Resort — Galveston",
        storeId: "MHR-GALVESTON-COTTAGE"
      },
      { id: "mhr-newport", name: "Hotel — Newport", storeId: "MHR-NEWPORT" },
      { id: "mhr-nassau", name: "Beach Resort — Nassau", storeId: "MHR-NASSAU" }
    ]
  },
  {
    id: "margaritaville-island-reserve",
    name: "Margaritaville Island Reserve Resort",
    locations: [
      { id: "mir-riviera-cancun", name: "Riviera Cancún", storeId: "MIR-RIVIERA-CANCUN" },
      { id: "mir-riviera-maya", name: "Riviera Maya", storeId: "MIR-RIVIERA-MAYA" },
      { id: "mir-roatan", name: "Roatán", storeId: "MIR-ROATAN" }
    ]
  },
  {
    id: "margaritaville-beach-intl",
    name: "Margaritaville Beach Resort (International)",
    locations: [
      { id: "mbi-flamingo", name: "Playa Flamingo — Costa Rica", storeId: "MBI-PLAYA-FLAMINGO" },
      {
        id: "mbi-caracol",
        name: "Residences Playa Caracol — Panama",
        storeId: "MBI-PLAYA-CARACOL"
      }
    ]
  },
  {
    id: "latitude-margaritaville",
    name: "Latitude Margaritaville",
    locations: [
      { id: "lat-daytona-tm", name: "Daytona Beach — Team Member", storeId: "TM-LAT-DAYTONA-BEACH" },
      { id: "lat-hilton-head-tm", name: "Hilton Head — Team Member", storeId: "TM-LAT-HILTON-HEAD" },
      { id: "lat-watersound-tm", name: "Watersound — Team Member", storeId: "TM-LAT-WATERSOUND" },
      { id: "lat-daytona-r", name: "Daytona Beach Residents", storeId: "R-LAT-DAYTONA-BEACH" },
      { id: "lat-hilton-head-r", name: "Hilton Head Residents", storeId: "R-LAT-HILTON-HEAD" },
      { id: "lat-watersound-r", name: "Watersound Residents", storeId: "R-LAT-WATERSOUND" }
    ]
  },
  {
    id: "margaritaville-at-sea",
    name: "Margaritaville at Sea",
    locations: [
      { id: "mas-islander", name: "Islander", storeId: "MAS-ISLANDER" },
      { id: "mas-paradise", name: "Paradise", storeId: "MAS-PARADISE" },
      { id: "mas-beachcomber", name: "Beachcomber", storeId: "MAS-BEACHCOMBER" }
    ]
  },
  {
    id: "air-margaritaville",
    name: "Air Margaritaville",
    locations: [
      { id: "amv-ft-lauderdale", name: "Fort Lauderdale", storeId: "AMV-FT-LAUDERDALE" },
      { id: "amv-miami", name: "Miami", storeId: "AMV-MIAMI" },
      { id: "amv-cancun", name: "Cancún, Mexico", storeId: "AMV-CANCUN" },
      { id: "amv-montego-bay", name: "Montego Bay, Jamaica", storeId: "AMV-MONTEGO-BAY" },
      { id: "amv-panama-city", name: "Panama City, Panama", storeId: "AMV-PANAMA-CITY" }
    ]
  },
  {
    id: "landshark-bar-grill",
    name: "LandShark Bar & Grill",
    locations: [
      { id: "lsg-atlantic-city", name: "Atlantic City", storeId: "LSG-ATLANTIC-CITY" },
      { id: "lsg-bayside", name: "Bayside", storeId: "LSG-BAYSIDE" },
      { id: "lsg-branson", name: "Branson, MO", storeId: "LSG-BRANSON" },
      { id: "lsg-daytona", name: "Daytona Beach", storeId: "LSG-DAYTONA-BEACH" },
      { id: "lsg-miramar", name: "Miramar Beach", storeId: "LSG-MIRAMAR-BEACH" },
      { id: "lsg-myrtle", name: "Myrtle Beach", storeId: "LSG-MYRTLE-BEACH" },
      { id: "lsg-north-myrtle", name: "North Myrtle Beach", storeId: "LSG-N-MYRTLE-BCH" },
      { id: "lsg-times-square", name: "Times Square", storeId: "LSG-TIMES-SQUARE" }
    ]
  },
  {
    id: "five-oclock-somewhere",
    name: "5 o'Clock Somewhere",
    locations: [
      { id: "fos-aruba", name: "Aruba", storeId: "FOS-ARUBA" },
      { id: "fos-curacao", name: "Curaçao", storeId: "FOS-CURACAO" },
      { id: "fos-miramar", name: "Miramar Beach", storeId: "FOS-MIRAMAR-BEACH" }
    ]
  },
  {
    id: "margaritaville",
    name: "Margaritaville",
    locations: [
      { id: "mv-atlanta", name: "Atlanta", storeId: "MV-ATLANTA" },
      { id: "mv-atlantic-city", name: "Atlantic City", storeId: "MV-ATLANTIC-CITY" },
      { id: "mv-boston", name: "Boston", storeId: "MV-BOSTON" },
      { id: "mv-chicago", name: "Chicago", storeId: "MV-CHICAGO" },
      { id: "mv-cleveland", name: "Cleveland", storeId: "MV-CLEVELAND" },
      { id: "mv-destin", name: "Destin", storeId: "MV-DESTIN" },
      { id: "mv-key-west", name: "Key West", storeId: "MV-KEY-WEST" },
      { id: "mv-mall-america", name: "Mall of America", storeId: "MV-MALL-OF-AMERICA" },
      { id: "mv-miami-bayside", name: "Miami Bayside", storeId: "MV-MIAMI-BAYSIDE" },
      { id: "mv-myrtle-beach", name: "Myrtle Beach", storeId: "MV-MYRTLE-BEACH" },
      { id: "mv-nashville", name: "Nashville", storeId: "MV-NASHVILLE" },
      { id: "mv-panama-city-beach", name: "Panama City Beach", storeId: "MV-PANAMA-CITY-BCH" },
      { id: "mv-pigeon-forge", name: "Pigeon Forge", storeId: "MV-PIGEON-FORGE" },
      { id: "mv-times-square-rest", name: "Restaurant — Times Square", storeId: "MV-REST-TIMES-SQ" },
      { id: "mv-san-antonio", name: "San Antonio", storeId: "MV-SAN-ANTONIO" },
      { id: "mv-syracuse", name: "Syracuse", storeId: "MV-SYRACUSE" },
      { id: "mv-tulsa", name: "Tulsa", storeId: "MV-TULSA" },
      { id: "mv-bahamas", name: "Restaurant — Bahamas (Paradise Island)", storeId: "MV-REST-BAHAMAS" },
      { id: "mv-cozumel", name: "Restaurant — Cozumel, Mexico", storeId: "MV-REST-COZUMEL" },
      { id: "mv-grand-turk", name: "Restaurant — Grand Turk, Turks & Caicos", storeId: "MV-REST-GRAND-TURK" },
      { id: "mv-mbj-rest", name: "Restaurant — Montego Bay, Jamaica", storeId: "MV-REST-MO-BAY" },
      { id: "mv-negril", name: "Restaurant — Negril, Jamaica", storeId: "MV-REST-NEGRIL" },
      { id: "mv-niagara", name: "Restaurant — Niagara Falls, ON, Canada", storeId: "MV-REST-NIAGARA" },
      { id: "mv-ocho-rios", name: "Restaurant — Ocho Rios, Jamaica", storeId: "MV-REST-OCHO-RIOS" },
      { id: "mv-st-thomas", name: "Restaurant — St. Thomas, USVI", storeId: "MV-REST-ST-THOMAS" }
    ]
  },
  {
    id: "margaritaville-vacation-club",
    name: "Margaritaville Vacation Club",
    locations: [
      { id: "mvc-nashville", name: "Nashville", storeId: "MVVC-NASHVILLE" },
      { id: "mvc-rio-mar", name: "Rio Mar", storeId: "MVVC-RIO-MAR" },
      { id: "mvc-st-thomas", name: "St. Thomas", storeId: "MVVC-ST-THOMAS" },
      { id: "mvc-atlanta", name: "Atlanta", storeId: "MVVC-ATLANTA" },
      { id: "mvc-desert-blue", name: "Desert Blue", storeId: "MVVC-DESERT-BLUE" }
    ]
  },
  {
    id: "margaritaville-corporate",
    name: "Corporate & other",
    locations: [
      { id: "corp-university", name: "Margaritaville University", storeId: "MVCORP-UNIVERSITY" },
      { id: "corp-call-center", name: "Margaritaville Call Center", storeId: "MVCORP-CALL-CENTER" },
      { id: "corp-retail", name: "Margaritaville Retail Team Members", storeId: "MVCORP-RETAIL" }
    ]
  }
];

/**
 * Job title → department bucket. Add or edit rows; `userType` is always store_employee (portal class).
 */
/** Legacy sample job-title rows (store lookup / mocks). Account request form loads roles from `/catalog/margaritaville/roles-by-store`. */
export const MARGARITAVILLE_JOB_TITLES: JobTitleDefinition[] = [
  { id: "server", label: "Server", department: "Front of House", userType: "store_employee" },
  { id: "bartender", label: "Bartender", department: "Front of House", userType: "store_employee" },
  { id: "host", label: "Host / Hostess", department: "Front of House", userType: "store_employee" },
  { id: "busser", label: "Busser / Runner", department: "Front of House", userType: "store_employee" },
  { id: "line-cook", label: "Line Cook", department: "Back of House", userType: "store_employee" },
  { id: "prep", label: "Prep Cook", department: "Back of House", userType: "store_employee" },
  { id: "sous-chef", label: "Sous Chef", department: "Back of House", userType: "store_employee" },
  { id: "rest-mgr", label: "Restaurant Manager", department: "Management", userType: "store_employee" },
  { id: "asst-mgr", label: "Assistant General Manager", department: "Management", userType: "store_employee" },
  { id: "gm", label: "General Manager", department: "Management", userType: "store_employee" },
  { id: "hk", label: "Housekeeping Attendant", department: "Rooms", userType: "store_employee" },
  { id: "fd-agent", label: "Front Desk Agent", department: "Front Office", userType: "store_employee" },
  { id: "fd-mgr", label: "Front Office Manager", department: "Front Office", userType: "store_employee" },
  { id: "activities", label: "Activities Coordinator", department: "Recreation", userType: "store_employee" },
  { id: "maintenance", label: "Maintenance Technician", department: "Engineering", userType: "store_employee" },
];

export function getBrandById(brandId: string): CatalogBrand | undefined {
  return MARGARITAVILLE_BRANDS.find((b) => b.id === brandId);
}

export function getJobTitleById(jobId: string): JobTitleDefinition | undefined {
  return MARGARITAVILLE_JOB_TITLES.find((j) => j.id === jobId);
}
